import type {
	NotificationColleagueScopeDefinition,
	NotificationGroupDefinition,
	ReplyColleagueScope,
} from "@/pages/Private/pages/Notifications/schema/notification";

export type NotificationThresholds = Record<string, unknown>;

/** A sub-toggle as the page shows it: the stored value, else the toggle's default. */
export const subToggleValue = (
	group: NotificationGroupDefinition,
	thresholds: NotificationThresholds | null | undefined,
	key: string
): boolean => {
	const stored = thresholds?.[key];

	if (typeof stored === "boolean") {
		return stored;
	}

	return group.subToggles?.find((sub) => sub.key === key)?.default ?? false;
};

/**
 * The thresholds a save writes: every sub-toggle and the colleague scope filled in with what the
 * page shows, so a stored row keeps its meaning when a default changes later.
 */
export const withGroupDefaults = (
	group: NotificationGroupDefinition,
	thresholds: NotificationThresholds | null | undefined
): NotificationThresholds => {
	const next: NotificationThresholds = { ...(thresholds ?? {}) };

	for (const sub of group.subToggles ?? []) {
		if (next[sub.key] === undefined) {
			next[sub.key] = sub.default;
		}
	}

	const scope = group.colleagueScope;

	if (scope) {
		next[scope.scopeKey] ??= scope.default;
		next[scope.idsKey] ??= [];
	}

	return next;
};

export const colleagueScopeOf = (
	definition: NotificationColleagueScopeDefinition,
	thresholds: NotificationThresholds | null | undefined
): ReplyColleagueScope => {
	const stored = thresholds?.[definition.scopeKey];

	return stored === "everyone" || stored === "selected" ? stored : definition.default;
};

export const colleagueIdsOf = (
	definition: NotificationColleagueScopeDefinition,
	thresholds: NotificationThresholds | null | undefined
): number[] => {
	const stored = thresholds?.[definition.idsKey];

	return Array.isArray(stored)
		? stored.filter((id): id is number => Number.isInteger(id) && id > 0)
		: [];
};

/**
 * The picker narrows the "…to colleagues" toggles, so it matters only while one of them is on —
 * with both off, nobody's replies reach the user whatever the picker says.
 */
export const isColleagueScopeActive = (
	group: NotificationGroupDefinition,
	thresholds: NotificationThresholds | null | undefined
): boolean =>
	!!group.colleagueScope?.appliesTo.some((key) => subToggleValue(group, thresholds, key));

export const toggleColleagueId = (ids: number[], id: number): number[] =>
	ids.includes(id) ? ids.filter((current) => current !== id) : [...ids, id];
